import formatFloatForDisplay from 'services/numbers/formatFloatForDisplay';
import numericStringToFloat from 'services/numbers/numericStringToFloat';
import { getSellingPriceQuantityFactor } from 'services/products/calculations/getSellingPriceForMargin';

/*
 * A selling price the user gives is the price of the row for ONE unit of the position
 * (e.g. 1,80 € for a m² of ceiling). It is a sum (€): the quantity and unit of the row do
 * not multiply it any more, so changing the quantity changes the expense and the margin,
 * not the price. A row priced per unit (e.g. 1,80 €/jm) keeps its price for a unit of the
 * position when its quantity changes: the price is turned into the sum it makes.
 *
 * This holds also for a material priced from its details (decided 14.10.2026): changing
 * only its quantity never changes its selling price. Not changed: a new row following the
 * target margin of the window, which is priced again for it.
 */

// As many decimals as the prices calculated from the details keep (HIDDEN_DECIMALS)
const DECIMALS = 6;

interface IPricedRow {
  followsTargetMargin?: boolean;
  quantity: string;
  sellingPrice: string;
  sellingPriceUnit: string | null;
}

/** The row with its selling price as the sum it makes for one unit of the position */
export const withSellingPriceAsSum = <T extends IPricedRow>(row: T): T => {
  if ([null, ''].includes(row.sellingPriceUnit)) {
    return row;
  }

  const factor = getSellingPriceQuantityFactor(
    row.sellingPriceUnit,
    numericStringToFloat(row.quantity)
  );

  return {
    ...row,
    sellingPrice:
      row.sellingPrice === ''
        ? ''
        : formatFloatForDisplay(
            numericStringToFloat(row.sellingPrice) * factor,
            DECIMALS
          ),
    sellingPriceUnit: '',
  };
};

/**
 * The row with a changed quantity: a price per unit is kept for one unit of the position,
 * as the sum it made with the earlier quantity.
 */
export const withQuantityKeepingPrice = <T extends IPricedRow>(
  previous: T,
  quantity: string
): T => {
  if (
    previous.followsTargetMargin ||
    [null, ''].includes(previous.sellingPriceUnit) ||
    previous.sellingPrice === ''
  ) {
    return { ...previous, quantity };
  }

  return { ...withSellingPriceAsSum(previous), quantity };
};
